<template>
	<section>
		<h3>Invoice/Quote Display</h3>

		<field v-if="layer != 'global' && !isFinalized"
			:label="mainToggleLabel"
			col-class="col-sm-6 col-md-6"
			:params="{type: 'boolean'}"
			:readonly="isFinalized"
			v-model="localSettings.override"
			v-on:change="overrideChange">
		</field>

		<p v-if="localSettings.override || layer == 'global'">Customize your invoice to show only what you want the customer to see.</p>

		<p v-else>This invoice will inherit from the {{ layer == 'customer' ? 'Global' : 'Customer' }} settings</p>

		<field label="Show 'Work Requested' field" 
			col-class="col-sm-6 col-md-6"
			:params="{type:'boolean'}"
			:readonly="readonly"
			v-model="localSettings.showWorkRequested"
			v-on:change="saveSettings">
		</field>

		<field label="Show lump sum only"
			col-class="col-sm-6 col-md-6"
			:params="{type:'boolean'}"
			:readonly="readonly"
			v-model="localSettings.lumpSum"
			v-on:change="saveSettings">
		</field>

		<template v-if="!localSettings.lumpSum">

			<field label="Show markup on invoice"
				col-class="col-sm-6 col-md-6"
				:params="{type:'boolean'}"
				:readonly="readonly"
				v-model="localSettings.materialsMarkup"
				v-on:change="saveSettings">
			</field>

			<field label="List all materials" 
				col-class="col-sm-6 col-md-6"
				:params="{type:'boolean'}"
				:readonly="readonly"
				v-model="localSettings.detailedMaterials"
				v-on:change="saveSettings">
			</field>

			<div class="row">
				<div class="offset-1">

					<field v-if="localSettings.materialsMarkup" 
						label="Adjust markup equally for all products"
						col-class="col-sm-8"
						:params="{type:'boolean'}"
						:readonly="readonly"
						v-model="localSettings.summaryMarkup"
						v-on:change="saveSettings">
					</field>

					<template v-if="localSettings.detailedMaterials">

						<h4>Customize Material Columns</h4>

						<field v-for="col in materialColumns"
							:label="col.label"
							:key="col.key"
							col-class="col-sm-8"
							:params="{type:'boolean'}"
							:readonly="readonly"
							v-model="localSettings['materialColumn:'+col.key]"
							v-on:change="saveSettings">
						</field>

						<hr>

						<field label="Break down assemblies into base materials"
							col-class="col-sm-8"
							:params="{type:'boolean'}"
							:readonly="readonly"
							v-model="localSettings.breakdownAssemblies"
							v-on:change="saveSettings">
						</field>

					</template>

					
				</div>
			</div>

			<field label="Show detailed labour breakdown" 
				col-class="col-sm-6 col-md-6"
				:params="{type:'boolean'}"
				:readonly="readonly"
				v-model="localSettings.detailedLabour"
				v-on:change="saveSettings">
			</field>

			<field label="Show detailed equipment breakdown" 
				col-class="col-sm-6 col-md-6"
				:params="{type:'boolean'}"
				:readonly="readonly"
				v-model="localSettings.detailedEquipment"
				v-on:change="saveSettings">
			</field>

			<field label="Show tasks as daily log" 
				col-class="col-sm-6 col-md-6"
				:params="{type:'boolean'}"
				:readonly="readonly"
				v-model="localSettings.tasksDailyLog"
				v-on:change="saveSettings">
			</field>

		</template>

		<div v-if="!isForService" class="mt-4">
			<h4>Project Billing</h4>

			<field label="Show detailed change orders" 
				col-class="col-sm-6 col-md-6"
				:params="{type:'boolean'}"
				:readonly="readonly"
				v-model="localSettings.detailedProjectChangeOrders"
				v-on:change="saveSettings">
			</field>
		</div>
	</section>
</template>
<script type="text/javascript">
import Field from "@/components/Field"

export default {
	name: "InvoiceDisplaySettings",
	components: {
		Field
	},
	props: [
		"documentSettings",
		"customerSettings",
		"globalSettings",
		"isForService",
		"isFinalized",
		"layer" //global, customer, document
	],
	data() {
		return {
			localSettings: {},
			materialColumns: [
				{key: "PriceBeforeMarkup", label: "Price Before Markup"},
				{key: "Markup", label: "Markup"},
				{key: "PriceAfterMarkup", label: "Price After Markup"},
				{key: "Qty", label: "Qty"},
				{key: "SubTotal", label: "Subtotal"}
			]
		}
	},
	watch: {
		documentSettings() {
			this.setLocalSettings()
		},
		customerSettings() {
			this.setLocalSettings()
		},
		globalSettings() {
			this.setLocalSettings()
		}
	},
	computed: {
		mainToggleLabel() {
			if (this.layer == 'customer')
				return "Over-ride Global Settings"
			else if (this.layer == 'document')
				return "Over-ride Customer Settings"

			return ""
		},
		readonly() {
			return this.layer != 'global' && (this.isFinalized || !this.localSettings.override)
		}
	},
	methods: {
		setLocalSettings(override) {
			let vm = this

			if (vm.layer == 'customer') 
				this.$set(this.localSettings,"override",this.customerSettings.override)
			else if (vm.layer == 'document') {
				this.$set(this.localSettings,"override",this.documentSettings.override)
			}

			if (typeof override != 'undefined') {
				this.$set(this.localSettings,"override",override)
			}

			let fields = [
				"detailedEquipment",
				"detailedLabour",
				"lumpSum",
				"showWorkRequested",
				"materialsMarkup",
				"detailedMaterials",
				"materialsQTYOnly",
				"summaryMarkup",
				//following settings aren't manageable directly on the invoice but need to be carried through
				"attachDirectly",
				"invoicingInstructions",
				"invoiceHardCopy",
				"paymentTerms",
				"invoicePortalLink",
				"invoiceRecipients",
				"usePriceAdjustment",
				"useMarkup",
				"detailedProjectChangeOrders",
				"tasksDailyLog",
				//"override"
			]

			this.materialColumns.forEach(matCol => {
				fields.push("materialColumn:"+matCol.key)
			})

			fields.forEach(field => {
				if (vm.layer == 'global') {
					vm.setFieldFromGlobal(field)
				}
				else if (vm.layer == 'customer') {
					if (vm.localSettings && "override" in vm.localSettings && vm.localSettings.override) 
						vm.setFieldFromCustomer(field)
					else 
						vm.setFieldFromGlobal(field)
				}
				else if (vm.layer == 'document') {
					if (vm.localSettings && "override" in vm.localSettings && vm.localSettings.override) 
						vm.setFieldFromDocument(field)
					else {
						if (vm.customerSettings && "override" in vm.customerSettings && vm.customerSettings.override) 
							vm.setFieldFromCustomer(field)
						else 
							vm.setFieldFromGlobal(field)
					}
				}
			})

			this.$forceUpdate()
		},
		setFieldFromGlobal(field) {
			if (this.globalSettings && field in this.globalSettings)
				this.$set(this.localSettings,field,this.globalSettings[field])
			else 
				this.$set(this.localSettings,field,false)
		},
		setFieldFromCustomer(field) {
			if (this.customerSettings && field in this.customerSettings)
				this.$set(this.localSettings,field,this.customerSettings[field])
			else
				this.setFieldFromGlobal(field)
		},
		setFieldFromDocument(field) {
			if (this.documentSettings && field in this.documentSettings)
				this.$set(this.localSettings,field,this.documentSettings[field])
			else
				this.setFieldFromCustomer(field)
		},
		overrideChange(value) {
			let vm = this

			if (!value)
				vm.setLocalSettings(value)

			vm.saveSettings()
		},
		saveSettings(value) {
			let vm = this

			this.$nextTick(() => {
				vm.$emit("settingsChange",this.localSettings)
				vm.$forceUpdate()
			})
		}
	},
	created() {
		this.setLocalSettings()
	}
}
</script>